import type { GroupKey } from './groups.js';

export interface Risk {
  id: string;
  group: GroupKey;
  /** 方块上显示的短名。贴图会自动缩放字号，但别超过 8 个字，太长了看不清 */
  title: string;
  /** 结算和讲解时的那一句情境，要具体、要有物件 */
  scene: string;
  /**
   * 能救你的真实条目 id。
   * **不是「同域就行」**：这里列的是原书里明确指向这件事的那几条。
   * 全部 id 都由 tools/verify-risks.cjs 对着 public/cards.json 校验，写错会构建失败。
   */
  keys: string[];
}

/**
 * 风险池。
 *
 * 早期只有 18 条，一局 19 手几乎跑完一遍，而且答案卡只有 25 张，
 * 玩家会觉得「翻来覆去就这几个」。现在按原书 34 章逐章铺开，
 * 覆盖 130 件事，答案卡用到 400 多张（占 672 条的六成以上）。
 *
 * 每条都能在原书里找到对应条目，keys 是真实条号（章-条），不是编的。
 */
const R = (
  id: string,
  group: GroupKey,
  title: string,
  scene: string,
  keys: string[]
): Risk => ({ id, group, title, scene, keys });

export const RISKS: Risk[] = [
  /* ===== 01 不要早死 ===== */
  R('crash', '身体', '车祸', '前排没系安全带，撞上的一瞬间人就出去了。', ['01-1', '01-2', '01-9']),
  R('co', '身体', '一氧化碳', '冬天在屋里烧煤取暖，睡着以后再没醒过来。', ['01-3', '01-4', '13-19']),
  R('mushroom', '身体', '毒蘑菇', '一篮看着眼熟的野生蘑菇，全家进了 ICU。', ['01-5']),
  R('ebike', '身体', '楼道起火', '电动车在楼道里充了一夜电，半夜起火封了唯一的出口。', ['01-6', '01-26', '13-24']),
  R('bp', '身体', '血压没人管', '血压高了好几年没人知道，某天早上直接脑出血。', ['01-7', '01-8', '02-12']),
  R('child-fall', '身体', '孩子坠楼', '纱窗挡不住一个三岁小孩，从六楼掉下去。', ['01-11', '01-10']),
  R('kid-water', '身体', '孩子溺水', '一转头的工夫，孩子已经沉到水里了。', ['01-12', '13-25']),
  R('elder-fall', '身体', '老人摔断髋', '浴室地滑，摔一跤之后就再也没能自己站起来。', ['01-13']),
  R('tetanus', '身体', '破伤风', '被锈铁钉扎了一下没管，一周后张不开嘴。', ['01-15', '13-13']),
  R('cervical', '身体', '宫颈癌', '一直没做过筛查，查出来时已经是晚期。', ['01-16', '01-18']),
  R('breast', '身体', '乳腺癌', '摸到硬块才去查，已经错过了最好的时候。', ['01-17']),
  R('colon', '身体', '肠癌', '大便带血当痔疮拖了半年，肠镜一做就是中晚期。', ['01-19', '01-27']),
  R('flu', '身体', '流感变重症', '一场流感引发肺炎，老人没扛过那个冬天。', ['01-20', '01-22', '01-21']),
  R('hp', '身体', '幽门螺杆菌', '胃一直不舒服忍着，查出来的时候已经萎缩性胃炎。', ['01-23']),
  R('lung', '身体', '肺癌', '抽了三十年烟，咳血才去拍片。', ['01-24', '02-1', '02-2']),
  R('suicide', '身体', '熬不过去', '夜里一个人待着，觉得没有别的办法了。', ['01-25', '01-32', '01-43', '29-11']),
  R('bleed-urine', '身体', '无痛血尿', '尿里带血，不疼，第二天就干净了，于是没去查。', ['01-27']),
  R('ed', '身体', '勃起变差', '当成「那方面的事」自己扛，其实是血管先出了问题。', ['01-28', '01-29']),
  R('std', '身体', '性病', '一次没做防护，之后什么也没查。', ['01-30', '01-31', '01-38']),
  R('hiv-pep', '身体', 'HIV 暴露', '发生了高危行为，72 小时之内还有机会。', ['13-38', '01-31']),
  R('fall-height', '身体', '高处坠落', '从脚手架上掉下来，进 ICU 的人多数活下来，代价是别的。', ['01-34']),
  R('sell-kidney', '身体', '卖肾', '少一个肾没什么，换来的钱几个月就没了。', ['01-35', '09-22']),
  R('scrap-metal', '身体', '来路不明的金属', '捡回来的「废铁」带着三叶草标志。', ['01-36']),
  R('gamble', '身体', '赌到借钱', '开始借钱还赌债，心情一直往下沉。', ['01-37', '08-44', '09-13']),
  R('bone', '身体', '骨质疏松', '轻轻一摔就骨折，之后再也直不起腰。', ['01-39', '01-40']),
  R('ice', '身体', '上冰面', '河面看着结实，走到中间冰就裂了。', ['01-41']),
  R('grinder', '身体', '角磨机伤人', '没装护罩，砂轮片崩开，碎片打在脸上。', ['01-42']),

  /* ===== 02 不要慢慢死 ===== */
  R('smoke', '身体', '烟戒不掉', '戒了三次，每次都靠忍，每次都复吸。', ['02-1', '02-3', '02-4', '02-5']),
  R('secondhand', '身体', '二手烟', '客人来了在客厅抽，孩子躲在房间里也躲不掉。', ['02-2']),
  R('sugar-drink', '身体', '含糖饮料', '一天两瓶，换成「无糖」之后照喝。', ['02-7']),
  R('betel', '身体', '嚼槟榔', '嚼了几年，张嘴越来越费劲。', ['02-8']),
  R('salt', '身体', '盐吃太多', '一家人的血压都偏高，锅里的盐一直没换过。', ['02-9', '01-7']),
  R('teeth', '身体', '牙周病', '刷牙出血好几年，牙一颗颗松动。', ['02-10']),
  R('sit', '身体', '久坐不动', '一天坐十个小时，回家继续躺着。', ['02-11', '02-15', '02-17']),
  R('sleep', '身体', '睡不够', '常年睡六个小时，周末补一天。', ['02-13', '03-3', '03-2']),
  R('processed-meat', '身体', '加工肉当饭吃', '火腿培根香肠午餐肉，冰箱里常年都有。', ['02-18']),
  R('alcohol', '身体', '酒停不下来', '每天都喝，一停就手抖心慌。', ['02-19', '02-20', '02-21']),
  R('ultra-processed', '身体', '超加工食品', '薯片方便面糕点速食，一周吃十几顿。', ['02-28']),
  R('coal-heat', '身体', '烧煤取暖', '屋里烧煤和柴，窗户关得严严的。', ['02-29', '01-3']),
  R('hot-drink', '身体', '滚烫的茶', '茶要趁烫喝，食道一直被烫着。', ['02-30']),
  R('obesity', '身体', '体重超标', 'BMI 到 29 了，衣服一年换一号。', ['02-32', '02-11', '02-16']),
  R('hood', '身体', '油烟不排', '炒菜不开抽油烟机，厨房一年四季油腻腻。', ['02-42']),
  R('oil', '身体', '散装自榨油', '小作坊散装花生油，便宜一半，没 SC 编号。', ['02-40', '02-41']),

  /* ===== 03 不要浪费精力 ===== */
  R('notify', '精力', '通知轰炸', '一天被打断两百次，到晚上一件正事都没做完。', ['03-1', '03-5', '03-6']),
  R('caffeine-pm', '精力', '下午还在喝咖啡', '下午三点一杯，晚上两点还醒着。', ['03-4', '03-3']),
  R('screen-night', '精力', '睡前刷屏幕', '睡前一小时在床上刷短视频，越刷越精神。', ['03-8', '03-9']),
  R('night-decision', '精力', '深夜做决定', '凌晨两点，把一封辞职信发了出去。', ['03-10']),
  R('overwork', '精力', '工时往上加', '每周干到 60 小时，活没多出来，人先垮了。', ['03-13', '04-14']),
  R('rumination', '精力', '反复回想', '同一件糟心事在脑子里转了一整晚。', ['03-14', '03-15']),
  R('toxic-people', '精力', '消耗型人际', '每次见完那个人都像被抽干。', ['03-16', '03-17']),
  R('low-mood', '精力', '情绪低落', '什么都不想干，躺了一周。', ['03-18', '03-25', '22-6']),
  R('compare', '精力', '刷同龄人动态', '每天看别人过得怎么样，看完更难受。', ['03-20', '03-21']),
  R('impostor', '精力', '觉得自己要露馅', '开会前反复想「所有人都会看出我不行」。', ['03-21', '03-22']),
  R('vent-anger', '精力', '靠砸东西发泄', '生气就砸，砸完更气。', ['03-23', '03-17']),

  /* ===== 04 不要浪费时间 ===== */
  R('no-plan', '精力', '「打算做」没落地', '说了三个月要开始，一次都没开始。', ['04-1', '04-2', '04-8']),
  R('sunk-cost', '精力', '沉没成本', '已经投进去这么多，舍不得停，又投了更多。', ['04-3', '04-4']),
  R('meetings', '精力', '会开不完', '一天四个会，一个议程都没有。', ['04-5', '04-6']),
  R('procrastinate', '精力', '拖延', '知道该做，一想到就难受，于是刷手机。', ['04-9', '04-10', '04-13']),
  R('commute', '精力', '通勤两小时', '每天来回三小时，一年就是一个月。', ['04-18', '04-14']),
  R('short-video', '精力', '刷短视频停不下来', '本来只看五分钟，抬头两小时过去了。', ['04-15', '04-16', '03-9']),

  /* ===== 05 不要浪费钱 ===== */
  R('auto-renew', '钱', '自动续费', '十几个自动续费没人管，一年白扣两千多。', ['05-1']),
  R('tax-refund', '钱', '个税汇算漏填', '专项附加扣除一项没填，多交的钱没人提醒你。', ['05-2']),
  R('rent-fund', '钱', '公积金睡大觉', '租了五年房，才知道公积金能提出来交房租。', ['05-3']),
  R('phone-plan', '钱', '套餐不重算', '宽带和手机套餐还是三年前的，一半的钱白交。', ['05-4']),
  R('lottery', '钱', '买彩票', '每期都买，一年下来一台冰箱没了。', ['05-5', '05-35']),
  R('high-yield', '钱', '高收益骗局', '「保本」「稳赚」，年化 18%。', ['05-6', '05-16', '05-45']),
  R('min-payment', '钱', '最低还款', '一直还最低，利息滚了两年。', ['05-7']),
  R('tipping', '钱', '打赏充值', '给主播刷礼物，给游戏充钱，一年几万。', ['05-8', '05-9', '05-10']),
  R('extend-warranty', '钱', '延长保修', '买手机加钱买了延保，出问题时条款一条都用不上。', ['05-11']),
  R('bottled-water', '钱', '常年订桶装水', '一桶十几块，一年下来够买台净水器。', ['05-14']),
  R('trade-often', '钱', '频繁交易', '行情一涨一跌就动手，手续费交了不少。', ['05-15', '05-37', '05-17']),
  R('all-in-one', '钱', '押在一只股票上', '所有钱都在一只票、一个平台上。', ['05-19', '05-16']),
  R('gym-card', '钱', '健身房年卡', '办了三年卡，去了十一次。', ['05-21']),
  R('prepay', '钱', '预付卡跑路', '充了一万块，第二个月店就关了。', ['05-22', '05-24']),
  R('impulse-buy', '钱', '冲动下单', '半夜看到「划线价」，直接下单。', ['05-23', '05-24']),
  R('wrong-insurance', '钱', '保险买错', '给孩子买了一堆，挣钱的那个人一份都没有。', ['05-25', '05-39', '05-40']),
  R('third-party', '钱', '三者险不够', '撞了人，交强险那点额度根本不够。', ['05-26']),
  R('no-buffer', '钱', '没有应急金', '一失业，下个月房租就交不上。', ['05-27', '07-1']),
  R('livestream-buy', '钱', '直播购物', '主播说的和收到的不一样，找不到人负责。', ['05-29', '05-30', '05-31']),
  R('gem', '钱', '买玉石手串', '当成投资买的，出手时才发现没人接。', ['05-33', '05-34', '05-35']),
  R('blind-box', '钱', '抽卡盲盒', '一直抽到出为止，钱不会回来了。', ['05-35', '05-8']),
  R('refund-agent', '钱', '找代理退保', '交了手续费，退保没办成，还被骗了信息。', ['05-43', '05-44']),
  R('crypto', '钱', '买虚拟货币', '熟人拉你一起投，说稳赚。', ['05-45', '05-6']),
  R('no-pension', '钱', '没单位不交养老', '自己干就一直没交，年限空着。', ['05-46', '07-18']),

  /* ===== 06 反面清单 ===== */
  R('multivitamin', '误区', '吃维生素防病', '每天一把复合维生素防心血管病，其实一点用没有。', ['06-1']),
  R('fish-oil', '误区', '吃鱼油防心病', '为了预防心血管病买了贵鱼油，效果和不吃一样。', ['06-2']),
  R('antioxidant', '误区', '抗氧化补剂', '为了防癌吃 β 胡萝卜素和维生素 E。', ['06-4', '06-3']),
  R('glucosamine', '误区', '氨糖治膝盖', '膝关节炎吃了两年氨糖，该疼还是疼。', ['06-5']),
  R('vitamin-c', '误区', '维C防感冒', '一感冒就大剂量维 C。', ['06-6']),
  R('pet-ct', '误区', 'PET-CT', '没症状去做「肿瘤标志物套餐」，查出个假阳性，折腾半年。', ['06-7', '06-18']),
  R('organic', '误区', '有机食品溢价', '为「更健康」多花一倍钱买有机。', ['06-9', '06-10']),
  R('tonic', '误区', '保健品调理', '花大钱买膏方和滋补品「调理身体」。', ['06-10', '06-13']),
  R('probiotic', '误区', '长期吃益生菌', '肠道没毛病，天天吃益生菌。', ['06-11']),
  R('cold-shower', '误区', '冷水澡增强免疫', '逼自己洗冷水澡，感冒次数没变。', ['06-12']),
  R('detox', '误区', '排毒产品', '买了排毒、清肠、酵素、碱性水。', ['06-13', '06-14']),
  R('fortune', '误区', '算命做决定', '花钱看塔罗决定要不要辞职。', ['06-15', '06-22']),
  R('blue-light', '误区', '防蓝光眼镜', '买了防蓝光眼镜「保护视力」。', ['06-16', '06-18']),
  R('uric-acid', '误区', '尿酸高就吃药', '体检查出尿酸高，但从没痛过，直接开始吃药。', ['06-19', '06-20']),
  R('no-calcium', '误区', '为防结石戒钙', '怕肾结石，把钙全戒了。', ['06-21', '06-30']),
  R('upgrade-race', '误区', '换房换车换圈子', '为了在周围人里往上挪一档，多花了一大笔。', ['06-24', '06-23']),
  R('willpower', '误区', '意志力像肌肉', '相信「用一会儿就没了」，于是干脆不用。', ['06-25', '04-10']),
  R('fasting', '误区', '16:8 轻断食', '硬撑轻断食，撑了三周，第四周报复性吃回来。', ['06-26', '02-32']),
  R('lifting', '误区', '学搬运姿势', '上了搬运培训，腰该疼还是疼。', ['06-29']),
  R('bone-soup', '误区', '骨折猛补钙', '骨折之后天天喝骨头汤。', ['06-30', '01-40']),

  /* ===== 07 没钱的时候怎么活 ===== */
  R('unemployed', '钱', '失业没领钱', '被裁了，失业保险金一直没去申领。', ['07-1', '07-12', '07-13']),
  R('wage-arrears', '钱', '被欠薪', '老板拖了半年工资，一直等着「下个月就发」。', ['07-2', '07-22', '07-3']),
  R('destitute', '钱', '走投无路', '身上没钱，也不知道有救助站和低保。', ['07-4', '07-6', '07-7', '07-21']),
  R('insurance-gap', '钱', '居民医保断了', '一年 400 块没交，住院全自费。', ['07-9', '07-10']),
  R('loan-for-illness', '钱', '重病去网贷', '家里有人得重病，第一反应是去借网贷。', ['07-10', '07-20']),
  R('job-scam', '钱', '求职中介收费', '交了押金和「培训费」，工作没影。', ['07-5', '07-15', '07-13']),
  R('housing', '钱', '住房困难', '房租占收入一半，不知道能排公租房。', ['07-16', '07-17']),
  R('training-loan', '钱', '培训贷', '「先培训后上岗」，签了才发现是贷款。', ['07-15', '09-6']),

  /* ===== 08 别把自己搭进去 ===== */
  R('hit-run', '法律', '事故后跑了', '撞了车，心里一慌就开走了。', ['08-1', '08-7']),
  R('fraud-transfer', '法律', '被骗转账', '电话里听见熟人的声音，钱转出去了。', ['08-2', '08-3', '08-4']),
  R('summoned', '法律', '被传唤', '被叫去问话，第一反应是自己去说清楚。', ['08-5', '08-6']),
  R('lend-card', '法律', '借卡给人', '朋友借银行卡走一笔钱，给两千好处费。', ['08-8', '09-5']),
  R('credit-check', '法律', '征信被冒用', '名下多出一张没办过的信用卡。', ['08-9', '08-45']),
  R('fight', '法律', '起冲突动手', '被推了一把，还手把人打伤。', ['08-10', '08-11', '09-19']),
  R('guarantee', '法律', '替人担保', '替朋友签了担保，朋友跑了，债主找上的是你。', ['08-18', '08-17', '12-2']),
  R('limitation', '法律', '维权过了期', '一直等着对方良心发现，等到时效过了。', ['08-19', '08-12']),
  R('gift', '法律', '大额赠与要不回', '恋爱期间转了几十万，分手想要回来。', ['08-23', '08-24']),
  R('premarital', '法律', '婚前没核底', '结婚以后才发现对方早就结过，钱也要不回来。', ['08-26', '08-25']),
  R('nominal-legal', '法律', '挂名法人', '朋友用你的身份证注册了公司，你成了法人。', ['08-28', '12-4']),
  R('dog', '法律', '遛狗不牵绳', '狗没牵绳，扑倒了一个老人。', ['08-30']),
  R('domestic', '法律', '被家暴', '被打之后被劝「家丑不可外扬」。', ['08-43', '08-16']),
  R('face-loan', '法律', '帮人刷脸', '熟人让你念了一段验证码，你背上八万块贷款。', ['08-45', '09-6']),
  R('burglary', '法律', '入室盗窃', '门锁是十年前的，门外一片黑。', ['08-46', '09-9']),
  R('rumor', '法律', '转发没核实的消息', '群里转了一条灾情消息，第二天被找上门。', ['09-1', '09-2', '09-3']),
  R('runaway-fee', '法律', '跑分兼职', '「兼职」要你用自己的卡收钱取现。', ['09-5', '09-6']),
  R('throw-out', '法律', '高空抛物', '从阳台扔下去一个酒瓶。', ['09-9']),
  R('drone', '法律', '无人机乱飞', '买了无人机没登记，在机场附近飞。', ['09-11']),
  R('peep', '法律', '出租屋装摄像头', '在出租屋里装了摄像头，退租没拆。', ['09-12']),
  R('debt-collect', '法律', '要债扣人', '要账要不回来，把人关在屋里不让走。', ['09-15', '08-12']),
  R('prostitution', '法律', '嫖娼', '以为罚点钱就了事。', ['09-23']),
  R('piracy', '法律', '搬运盗版', '搬运影视剧、分享网盘资源，不收钱也侵权。', ['09-24', '09-25']),
  R('self-defense', '法律', '带家伙防身', '车里放了一根甩棍「防身」。', ['09-26', '08-10']),

  /* ===== 10 恋爱和结婚划不划算 ===== */
  R('obsess', '家庭', '死磕一个人', '对方已经明确拒绝，还是天天发消息。', ['10-1', '10-2']),
  R('pickup-lines', '家庭', '学话术', '背了一堆话术，见面聊十分钟就冷场。', ['10-5', '10-3']),
  R('parents-pay', '家庭', '父母出资没说清', '父母出首付买房，没写是借是送，离婚时吵翻。', ['10-11', '10-10']),
  R('spouse-debt', '家庭', '配偶大额借款', '配偶在外面借了一大笔，债主找上你。', ['10-12', '08-44']),
  R('checkup-couple', '家庭', '婚前没婚检', '有重大疾病没有在登记前告诉对方。', ['10-14', '10-13']),
  R('divorce-cost', '家庭', '算不清退出成本', '以为离婚说走就走，结果冷静期和诉讼条件都不清楚。', ['10-15', '10-16']),
  R('cold-war', '家庭', '冷战反复吵', '一吵架就冷战，谁也不先开口，一年十几次。', ['10-20', '10-17']),

  /* ===== 11 程序员和技术人容易踩的红线 ===== */
  R('cheat', '法律', '做游戏外挂', '接了个「只读屏幕不碰内存」的 AI 辅助单子。', ['11-2', '11-1']),
  R('ticket-bot', '法律', '写抢票脚本', '写了秒杀和薅羊毛脚本，觉得只是「自动化点按钮」。', ['11-3', '11-1']),
  R('crawler', '法律', '爬虫越界', '绕过了反爬，还爬了带个人信息的页面。', ['11-4', '11-1']),
  R('delete-db', '法律', '离职删库', '公司欠薪，走之前把库删了。', ['11-6', '19-8']),
  R('take-code', '法律', '带走源码', '离职把源码和客户名单拷进了个人网盘。', ['11-7', '11-13']),
  R('pentest', '法律', '未授权渗透', '「出于好意」测了别人的系统，还发了漏洞细节。', ['11-9', '11-10']),
  R('vpn-tool', '法律', '卖翻墙工具', '顺手帮人搭了几个节点，收了点钱。', ['11-11']),
  R('noncompete', '法律', '竞业协议', '签了竞业，离职后公司不给补偿，就一直干等着。', ['11-12']),
  R('gpl', '法律', '开源许可证', '用了 GPL 代码做产品，当成「没人管」直接闭源发布。', ['11-14', '11-13']),
  R('icp', '法律', '网站没备案', 'App 上线了才发现要 ICP 备案和等级保护。', ['11-16', '11-15']),
  R('ai-service', '法律', 'AI服务没备案', '对公众提供生成式 AI 服务，没做安全评估和算法备案。', ['11-17', '11-19']),
  R('ai-relay', '法律', 'AI 中转站', '低价转卖境外大模型接口，接了单就开工。', ['11-19', '14-10']),

  /* ===== 12 创业与做生意 ===== */
  R('borrow-startup', '钱', '借钱创业', '动了家底，还给公司贷款签了个人担保。', ['12-1', '12-2']),
  R('entity', '钱', '主体选错', '用个体户接了大单，赔的时候赔到底。', ['12-3']),
  R('franchise', '钱', '加盟没查备案', '交了加盟费，合同里没有冷静期，总部也查不到备案。', ['12-5', '12-24']),
  R('license', '钱', '无证开张', '证还没下来就先开门做生意。', ['12-7', '12-8']),
  R('food-label', '钱', '食品标签不全', '自己装袋卖，标签上没写生产日期和配料表。', ['12-9', '12-10', '12-11']),
  R('zero-declare', '钱', '忘了零申报', '领了执照没收入，就没报税，被罚了。', ['12-12', '12-14']),
  R('fake-invoice', '钱', '发票乱开', '客户要发票，多开了一点金额。', ['12-13', '12-14']),
  R('no-contract-hire', '钱', '用工不签合同', '招了人第一个月没签书面合同。', ['12-16', '12-17']),
  R('no-exit', '钱', '亏了不注销', '生意黄了，公司就那么放着，年报也不报。', ['12-23', '12-12']),

  /* ===== 13 紧急情况 ===== */
  R('cpr', '身体', '有人没呼吸', '有人倒在路上，没呼吸了，周围没人会按。', ['13-1', '13-2']),
  R('stroke', '身体', '中风信号', '突然嘴歪、一侧胳膊没劲、说话说不清，家里人说先躺一会儿。', ['13-3', '13-4', '13-5']),
  R('chest-pain', '身体', '胸痛硬扛', '胸口压着疼超过十五分钟，还想着睡一觉就好。', ['13-7', '13-8']),
  R('worst-headache', '身体', '最疼的头痛', '一小时内到最疼的那种头痛。', ['13-9', '13-10']),
  R('leg-swelling', '身体', '腿突然肿', '一条腿突然肿起来发紧，还喘不上气。', ['13-11']),
  R('bleeding', '身体', '大出血', '血止不住，还想着先送去医院再说。', ['13-12', '13-40']),
  R('dog-bite', '身体', '被狗咬', '被狗咬破了皮，随便冲了两下。', ['13-13', '13-29']),
  R('burn', '身体', '烫伤', '烫伤之后抹了牙膏和酱油。', ['13-14', '13-21']),
  R('anaphylaxis', '身体', '过敏性休克', '全身起疹、喘不上气、发晕。', ['13-15', '13-20']),
  R('seizure', '身体', '抽搐', '有人抽搐倒地，旁边人往他嘴里塞东西。', ['13-16']),
  R('hypoglycemia', '身体', '低血糖', '糖尿病人突然发抖出冷汗、说话不清。', ['13-17']),
  R('electric', '身体', '触电', '有人触电，第一反应是上手去拉。', ['13-18', '13-21']),
  R('poison', '身体', '误服', '孩子喝了清洁剂，家长第一反应是催吐。', ['13-20', '34-4']),
  R('heatstroke', '身体', '中暑', '高温下头晕恶心、不出汗了。', ['13-22', '13-23']),
  R('fire', '身体', '火灾', '楼道起火，有人坐电梯往下跑。', ['13-24', '01-26']),
  R('choke', '身体', '噎住', '有人噎住说不出话，旁边人只会拍背。', ['13-26', '13-43']),
  R('hypothermia', '身体', '失温', '发抖、说话含糊、走路打晃，还穿着湿衣服。', ['13-28', '13-35']),
  R('snake', '身体', '被蛇咬', '被蛇咬之后用嘴去吸。', ['13-29']),
  R('earthquake', '身体', '地震', '地震时先往楼下冲。', ['13-30', '13-32']),
  R('wild', '身体', '野外遇险', '在戈壁里迷路，车也坏了，人往外走。', ['13-27', '13-31', '13-33']),
  R('tick', '身体', '蜱虫叮住', '被蜱虫叮住，用油去闷、用火烧。', ['13-34', '13-35']),
  R('assault', '身体', '被性侵', '被性侵之后先洗了澡、洗了衣服。', ['13-42', '13-38']),

  /* ===== 14 账号与信息安全 ===== */
  R('2fa', '法律', '账号没二次验证', '邮箱和支付账号还是只靠一个密码。', ['14-1', '14-2']),
  R('phone-lost', '法律', '手机丢了', '手机丢了，先忙着找手机，没挂失 SIM 卡。', ['14-4', '14-5', '14-3']),
  R('app-consent', '法律', 'App 全部同意', '为了用一个 App，点了「全部同意」。', ['14-7', '14-9', '14-8']),

  /* ===== 15 租房与买房 ===== */
  R('deposit', '钱', '押金要不回', '退租那天房东挑出一堆毛病，押金一分没退。', ['15-1', '15-6']),
  R('evict', '钱', '被逼着搬走', '房东断了水电、换了锁，让你赶紧搬。', ['15-2', '15-5']),
  R('apartment-fund', '钱', '长租公寓暴雷', '一次性付了一年房租，公司跑了。', ['15-4', '15-3']),
  R('partition', '钱', '租了隔断房', '租的是厨房改的房间，阳台也住了人。', ['15-8', '15-9']),

  /* ===== 16 得了慢性病之后 ===== */
  R('stop-med', '身体', '自己停药', '感觉好了就把药停了，血压又上去了。', ['16-1', '16-4', '02-12']),
  R('chronic-cert', '身体', '慢特病没认定', '长期吃药全自费，不知道有门诊慢特病认定。', ['16-2', '16-5']),
  R('diabetes-eye', '身体', '糖尿病没查眼底', '确诊糖尿病三年，一次眼底都没查过。', ['16-7', '16-3']),
  R('gout', '身体', '痛风不吃药', '痛的时候吃药，不痛就停，尿酸一直没降下来。', ['16-9', '16-1']),

  /* ===== 17 家里有老人 ===== */
  R('guardian', '家庭', '老人没指定监护人', '老人还清醒的时候，谁也没提过将来谁管。', ['17-1', '17-2']),
  R('elder-invest', '家庭', '投资养老骗局', '让老人先交钱买床位、买养老公寓。', ['17-5', '17-6', '17-4']),
  R('bedsores', '家庭', '卧床长压疮', '老人卧床半年，尾骨烂了一个洞。', ['17-8', '17-7']),
  R('cataract', '家庭', '白内障拖', '老人看路已经费劲了，还说「不着急做」。', ['17-9']),

  /* ===== 18 养孩子划不划算 ===== */
  R('child-subsidy', '家庭', '育儿补贴没领', '生了孩子，每年 3600 的育儿补贴一直没去领。', ['18-1', '18-2']),
  R('pregnant-fired', '家庭', '孕期被辞退', '怀孕之后被降薪、被劝退。', ['18-3', '19-6']),

  /* ===== 19 在职、离职和工伤 ===== */
  R('overtime', '工作', '加班费被吞', '三年加班费一分没给，离职时才发现自己什么证据都没有。', ['19-1', '19-8']),
  R('annual-leave', '工作', '年假没休', '攒了二十天年假没休，公司说年底作废。', ['19-2', '19-8']),
  R('probation', '工作', '试用期被坑', '试用期六个月，工资打七折。', ['19-3', '19-8']),
  R('layoff-n', '工作', '被裁算不清 N', '公司让签「个人原因主动辞职」。', ['19-4', '19-5', '19-7']),
  R('work-injury', '工作', '工伤不认', '上班受伤，单位说走医保就行，工伤待遇全没了。', ['19-11', '19-13', '19-12']),
  R('disability-grade', '工作', '没做劳动能力鉴定', '伤好了就回去上班，伤残等级一直没去鉴定。', ['19-14', '19-15', '24-9']),
  R('occupational', '工作', '职业病', '在粉尘车间干了八年，从来没做过职业健康检查。', ['19-9', '19-10']),
  R('severance-tax', '工作', '补偿金多交税', '拿到离职补偿，不知道有一部分是免税的。', ['19-18', '19-17']),
  R('social-transfer', '工作', '社保没转', '换城市工作，养老和医保关系一直没转。', ['19-19', '07-18']),

  /* ===== 20 刚出生的孩子怎么带 ===== */
  R('infant-sleep', '家庭', '婴儿睡眠安全', '床上铺了软枕头和毛绒玩具，孩子趴着睡。', ['20-1', '20-9']),
  R('vaccine-miss', '家庭', '疫苗漏打', '疫苗本上漏了两针，一直没去补。', ['20-3', '20-2', '20-7']),
  R('formula', '家庭', '冲奶粉水温不对', '用温水和奶粉，冲好放着慢慢喂。', ['20-5', '20-4']),
  R('honey', '家庭', '给婴儿喂蜂蜜', '一岁不到，喂了一勺蜂蜜。', ['20-6', '20-4']),
  R('infant-fever', '家庭', '婴儿发烧在家观察', '三个月大的孩子烧到 38 度，先在家物理降温。', ['20-8', '20-13']),

  /* ===== 21 出国、旅行与境外安全 ===== */
  R('travel-warning', '身体', '去了危险地区', '出发前没查安全提醒，落地就出事。', ['21-1', '21-2']),
  R('overseas-job', '身体', '境外高薪招聘', '「境外高薪招聘」，去了才知道是做电诈。', ['21-5', '21-10']),
  R('drive-abroad', '身体', '境外自驾', '拿着网上买的「国际驾照」在国外租车。', ['21-8', '21-4']),
  R('passport-lost', '身体', '护照丢了', '护照在境外丢了，不知道先报警还是先补证。', ['21-7', '21-11']),

  /* ===== 22 怎么放松 ===== */
  R('exit-sign', '精力', '娱乐场所没看出口', 'KTV 的安全出口被锁了，也没换一家。', ['22-1', '22-5']),
  R('price-list', '精力', '先玩后算', '点单前没要价目表，结账时被宰。', ['22-2']),
  R('stranger-gift', '精力', '陌生人递东西', '接了别人递的烟弹，喝了离开过视线的饮料。', ['22-3', '22-4']),
  R('vent-place', '精力', '密室在地下二层', '密室开在居民楼地下二层，通道只有一个。', ['22-5', '22-1']),

  /* ===== 23 学什么技能划算 ===== */
  R('underage-work', '工作', '十六岁前去打工', '不满十六周岁就进厂，出了事没人管。', ['23-1', '23-5']),
  R('cert-scam', '工作', '花钱考没用的证', '花几千块考了个不在目录里的「资格证」。', ['23-8', '23-9', '23-12']),
  R('title-fraud', '工作', '找中介代评职称', '找中介代评职称，还买了论文代写。', ['23-22', '23-20']),
  R('bad-study', '工作', '划重点当学法', '反复读、写摘要、划重点，考完就忘。', ['23-16', '23-19', '23-15']),

  /* ===== 24 看病 ===== */
  R('skip-level', '身体', '不按级别就医', '小病直奔三甲，排了一整天队。', ['24-1', '24-2', '24-3']),
  R('records', '身体', '病历没存', '看完病什么记录都没留，再查一次全重做。', ['24-5', '24-6']),
  R('emergency-queue', '身体', '急重去排队挂号', '胸口疼还在挂号窗口排队。', ['24-7', '24-8']),
  R('dental-implant', '身体', '种牙图便宜', '只看低价，没问包干价里含什么。', ['24-12', '24-11']),

  /* ===== 25 人走了以后 ===== */
  R('death-cert', '家庭', '死亡证明没办', '人在家里走的，家属不知道该找谁开证明。', ['25-2', '25-1']),
  R('funeral-fee', '家庭', '殡葬被乱收费', '强制搭售、标价外加价，一项拆成几项收。', ['25-6', '25-7', '25-8']),
  R('unclaimed', '家庭', '各处钱没取', '公积金、社保、工伤待遇散在各处，没人去取。', ['25-9', '25-10']),

  /* ===== 26 做一个网站或平台 ===== */
  R('platform-money', '法律', '平台自己收钱', '平台收了买家的钱再转给卖家。', ['26-1', '26-2']),
  R('server-filing', '法律', '服务器没备案', '服务器放在境内，一直没备案。', ['26-4', '26-3']),
  R('user-content', '法律', '用户内容不管', '用户发的内容没有审核机制，也没有举报入口。', ['26-6', '26-5', '26-7']),
  R('minor-live', '法律', '给未成年人开直播', '给未满十六岁的孩子开了直播打赏。', ['26-8', '30-5']),

  /* ===== 27 怀孕和生产 ===== */
  R('folic-acid', '身体', '没补叶酸', '准备怀孕才开始注意，叶酸一直没补。', ['27-1', '27-5']),
  R('prenatal', '身体', '产检漏项', '孕早期没建册，把免费的筛查项全错过了。', ['27-2', '27-3']),
  R('pregnant-smoke', '身体', '孕期烟酒', '怀孕了还在抽烟，家里人也在屋里抽。', ['27-4', '02-2']),
  R('gdm', '身体', '糖筛没做', '孕 24 周该做的糖筛没做，嫌喝糖水麻烦。', ['27-6', '27-7']),
  R('water-break', '身体', '破水还走动', '破水了还站起来走动、洗澡。', ['27-8', '27-7']),
  R('birth-cert', '身体', '出生证明没办', '出院前没办《出生医学证明》，后面处处卡住。', ['27-12', '27-15', '27-14']),
  R('newborn-screen', '身体', '拒绝新生儿筛查', '足跟血和听力筛查都没做。', ['27-13', '20-13']),
  R('postpartum', '身体', '产后没复查', '出了月子就忙起来，42 天复查一直没去。', ['27-16', '29-1']),

  /* ===== 28 别为了外形把身体搞坏 ===== */
  R('crash-diet', '身体', '极端节食', '靠断食和催吐控制体重。', ['28-1', '02-32']),
  R('illegal-aesthetic', '身体', '黑医美', '在没资质的机构打针、埋线，动手的人也没有医师证。', ['28-2', '28-3']),
  R('diet-pill', '身体', '买减肥药', '网购承诺「快速瘦」的减肥咖啡和瘦身糖果。', ['28-4', '28-6', '28-7']),
  R('steroid', '身体', '增肌针', '为了长肌肉用合成代谢类固醇。', ['28-5', '28-7']),
  R('diy-braces', '身体', '网购牙套自己戴', '网上买了牙套，自己在家戴。', ['28-9', '28-8']),

  /* ===== 29 遭遇重大打击之后 ===== */
  R('bereaved-alone', '精力', '亲人刚走', '亲人刚走的那几天，一个人关在屋里。', ['29-1', '29-5', '29-6']),
  R('jobless-routine', '精力', '失业后作息乱', '失业之后整天待在家里，昼夜颠倒。', ['29-3', '29-10']),
  R('grief-stuck', '精力', '哀伤卡住', '过了半年还在原地，日子过不下去。', ['29-8', '29-9', '29-4']),
  R('big-decision', '精力', '变故后做大决定', '刚出事就想卖房、辞职、搬家。', ['29-12', '29-2']),

  /* ===== 30 上学以后的孩子 ===== */
  R('child-pain', '家庭', '孩子剧痛忍着', '孩子说肚子越来越疼，家长说等放学再说。', ['30-1', '30-2']),
  R('bully', '家庭', '孩子被欺凌', '孩子被抢钱、被传谣，家长想着「小孩子打闹」。', ['30-3', '30-8']),
  R('myopia', '家庭', '孩子近视', '每天户外不到半小时，度数一年涨一百。', ['30-4', '30-6', '30-12']),
  R('screen-kid', '家庭', '孩子屏幕时间', '三岁就抱着平板吃饭。', ['30-5', '30-14']),
  R('kid-depression', '家庭', '孩子抑郁', '孩子说撑不住了，家长说「就是不想上学」。', ['30-8', '30-11']),
  R('boot-camp', '家庭', '送戒网瘾学校', '孩子爱打游戏，打算送去封闭管理的特训营。', ['30-18', '30-17']),
  R('hit-kid', '家庭', '打骂孩子', '孩子不听话就动手打、骂他笨。', ['30-16', '30-17']),

  /* ===== 31 十八岁之后有哪几条路 ===== */
  R('military-register', '工作', '没做兵役登记', '十八岁那年没做兵役登记，后来升学卡住。', ['31-2', '31-3']),
  R('veteran-report', '工作', '退役没报到', '退役之后三十日内没去报到。', ['31-5', '31-6', '31-4']),
  R('flexible-insurance', '工作', '灵活就业没参保', '自己干，养老和医疗一直没交。', ['31-11', '31-12', '05-46']),
  R('overseas-labor', '工作', '出国打工没查资质', '找了家中介出国打工，中介收了押金。', ['31-14', '21-5']),

  /* ===== 32 出国留学 ===== */
  R('school-cert', '工作', '学校不在认证名单', '交了学费才发现学校不在留服中心的认证名单里。', ['32-1', '32-10']),
  R('work-hours', '工作', '留学打工超时', '为了多挣钱，每周打工超过签证规定的小时数。', ['32-3', '32-4']),
  R('oshc-gap', '工作', '留学保险断档', '保险中间断了两个月，落地被查。', ['32-7', '32-8']),
  R('cert-time', '工作', '回国认证没留时间', '回国当天就要用学历认证，结果要等 20 个工作日。', ['32-9', '32-5']),

  /* ===== 33 残疾之后怎么活 ===== */
  R('sci-headache', '身体', '脊髓损伤头痛', '脊髓损伤之后突然剧烈头痛、出汗。', ['33-1', '33-4']),
  R('disability-suicide', '身体', '致残后的低谷', '致残之后的头几年，人一直往下沉。', ['33-2', '33-3', '29-4']),
  R('wheelchair-sore', '身体', '轮椅压疮', '长期坐轮椅，坐垫没换，坐骨一直红着。', ['33-5', '33-16']),
  R('fake-cure', '身体', '治好瘫痪的骗局', '有人推销「能治好瘫痪、失明、耳聋」的疗法。', ['33-6', '33-17']),
  R('disability-benefit', '身体', '残疾人福利没申请', '残疾人证办下来了，能申请的东西一项都没问。', ['33-7', '33-9', '33-11']),

  /* ===== 34 家里的常备药别吃出事 ===== */
  R('acetaminophen', '身体', '感冒药叠加', '两盒感冒药一起吃，成分表没看。', ['34-1', '34-10']),
  R('kid-aspirin', '身体', '给孩子吃阿司匹林', '孩子发烧，喂了含安乃近的感冒药。', ['34-2', '34-4']),
  R('ibuprofen', '身体', '布洛芬伤胃', '胃出过血，还在吃布洛芬止痛。', ['34-3', '34-5']),
  R('antibiotic', '身体', '感冒吃抗生素', '普通感冒就找医生要抗生素。', ['34-7', '34-6']),
  R('diarrhea', '身体', '拉肚子吃止泻药', '五岁以下的孩子拉肚子，先给了止泻药。', ['34-8', '13-20']),
  R('painkiller-overuse', '身体', '止痛药吃太多', '一个月吃了二十天止痛药，头越来越痛。', ['34-9', '34-3']),
  R('cephalosporin', '身体', '头孢配酒', '吃着头孢，饭桌上还是喝了酒。', ['34-11', '02-19']),
];
